import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { EventCard3D } from "@/components/events/EventCard3D";
import { ArrowRight } from "lucide-react";
import * as LucideIcons from "lucide-react";

interface CountdownTime {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  isPast: boolean;
}

const calculateCountdown = (dateString: string | null): CountdownTime | null => {
  if (!dateString) return null;
  
  const eventDate = new Date(dateString);
  const now = new Date();
  const diff = eventDate.getTime() - now.getTime();
  
  if (diff <= 0) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0, isPast: true };
  }
  
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diff % (1000 * 60)) / 1000);
  
  return { days, hours, minutes, seconds, isPast: false };
};

interface CategoryData {
  id: string;
  name_ar: string;
  name_en: string;
  icon: string;
  color: string;
  path: string;
}

interface DynamicCategoryPageProps {
  categoryPath: string;
}

const DynamicCategoryPage = ({ categoryPath }: DynamicCategoryPageProps) => {
  const { i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  const [countdowns, setCountdowns] = useState<Record<string, CountdownTime | null>>({});

  // Fetch category info by path
  const { data: category } = useQuery({
    queryKey: ["category", categoryPath],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("categories" as any)
        .select("*")
        .eq("path", categoryPath)
        .maybeSingle();
      
      if (error) throw error;
      return data as unknown as CategoryData | null;
    },
  });

  // Fetch events for this category - extract slug from path
  const { data: events = [], isLoading } = useQuery({
    queryKey: ["category-events", categoryPath],
    queryFn: async () => {
      // Extract category slug from path (e.g., /category/my-category -> my-category)
      const categorySlug = categoryPath.replace("/category/", "").replace("/", "");
      
      const { data, error } = await supabase
        .from("scraped_events")
        .select("*")
        .eq("is_published", true)
        .eq("category", categorySlug)
        .order("date", { ascending: true });
      
      if (error) throw error;
      return data;
    },
  });

  // Update countdowns
  useEffect(() => {
    const updateCountdowns = () => {
      const newCountdowns: Record<string, CountdownTime | null> = {};
      events.forEach((event) => {
        newCountdowns[event.id] = calculateCountdown(event.date);
      });
      setCountdowns(newCountdowns);
    };

    updateCountdowns();
    const interval = setInterval(updateCountdowns, 1000);
    return () => clearInterval(interval);
  }, [events]);

  const getIconComponent = (iconName: string) => {
    const IconComponent = (LucideIcons as any)[iconName];
    return IconComponent || LucideIcons.Calendar;
  };

  const IconComponent = category ? getIconComponent(category.icon) : LucideIcons.Calendar;

  return (
    <div className="min-h-screen bg-gray-50" dir={isRTL ? "rtl" : "ltr"}>
      <Navbar />
      
      <div className="container py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Link 
            to="/" 
            className="flex items-center gap-2 text-gray-500 hover:text-primary transition-colors"
          >
            <ArrowRight className={`w-5 h-5 ${isRTL ? "" : "rotate-180"}`} />
            <span>{isRTL ? "الرئيسية" : "Home"}</span>
          </Link>
        </div>

        <div className="flex items-center gap-4 mb-8">
          {category && (
            <div className={`${category.color} p-4 rounded-xl bg-white shadow-sm`}>
              <IconComponent size={40} />
            </div>
          )}
          <div>
            <h1 className="text-3xl font-bold text-gray-800">
              {category ? (isRTL ? category.name_ar : category.name_en) : "..."}
            </h1>
            <p className="text-gray-500 mt-1">
              {isRTL 
                ? `${events.length} فعالية متاحة`
                : `${events.length} events available`
              }
            </p>
          </div>
        </div>

        {/* Events Grid */}
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="animate-pulse bg-white rounded-3xl h-96"></div>
            ))}
          </div>
        ) : events.length === 0 ? (
          <div className="text-center py-16">
            <div className="text-gray-400 mb-4">
              <IconComponent size={64} className="mx-auto opacity-50" />
            </div>
            <h3 className="text-xl font-semibold text-gray-600 mb-2">
              {isRTL ? "لا توجد فعاليات حالياً" : "No events available"}
            </h3>
            <p className="text-gray-400">
              {isRTL 
                ? "سيتم إضافة فعاليات جديدة قريباً"
                : "New events will be added soon"
              }
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {events.map((event) => (
              <EventCard3D
                key={event.id}
                event={event}
                isRTL={isRTL}
                countdown={countdowns[event.id] || null}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DynamicCategoryPage;
